import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { languageOptions } from '@/i18n/options';
import ArrowDownIcon from '@/components/icons/ArrowDownIcon';
import { Button } from '../ui/button';
import { useCurrencyStore } from '@/store/useCurrencyStore';
import { cn } from '@/lib/utils';
import { t } from 'i18next';

type Props = {
	showCurrency?: boolean;
};

const ESelectLanguage: FC<Props> = ({ showCurrency }) => {
	const [isOpen, setIsOpen] = useState(false);
	const { i18n } = useTranslation();
	const { currentCurrency, currencies, setCurrentCurrency } =
		useCurrencyStore();

	useEffect(() => {
		if (!currentCurrency) {
			setCurrentCurrency('USD');
		}
	}, [currentCurrency]);

	const handleOpenToggler = () => {
		setIsOpen((prev) => !prev);
	};

	return (
		<DropdownMenu onOpenChange={handleOpenToggler}>
			<DropdownMenuTrigger
				asChild
				className={'px-4 py-3 rounded-semi-xl'}>
				<Button
					variant='outline'
					className='whitespace-nowrap'>
					<span className='w-6 text-center'>
						{languageOptions[i18n.language].text}
					</span>
					{showCurrency && (
						<>
							<span>·</span>
							<span className='w-4 text-center'>
								{currentCurrency && currencies[currentCurrency]}
							</span>
						</>
					)}
					<div
						className={`transition-transform duration-50 ease-in-out ${
							isOpen ? '-rotate-180' : 'rotate-0'
						}`}>
						{<ArrowDownIcon />}
					</div>
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuContent
				align='end'
				className={
					'w-60 p-4 flex flex-col gap-2 rounded-2.5xl border border-popover-100 bg-popover-10 shadow-custom'
				}>
				<div className='flex flex-col gap-2'>
					<h4 className='text-sm text-popover-800'>{t('header.selectOptions.language')}</h4>
					{Object.entries(languageOptions).map(([key, value]) => (
						<button
							key={key}
							className='px-3 py-2.5 flex items-center justify-between gap-2.5 rounded-semi-xl hover:bg-primary-color-50 active:bg-primary-color-100 cursor-pointer'
							onClick={() => i18n.changeLanguage(key)}>
							{value.text}
							<div
								className={cn(
									'size-4 border-primary-color rounded-full transition-all',
									languageOptions[i18n.language].text ===
										value.text
										? 'border-4'
										: 'border',
								)}
							/>
						</button>
					))}
				</div>
				{showCurrency && (
					<div className='flex flex-col gap-2'>
						<h4 className='text-sm text-popover-800'>{t('header.selectOptions.currency')}</h4>
						{Object.entries(currencies).map(([key, value]) => (
							<button
								key={key}
								className='p-3 flex items-center justify-between gap-2.5 rounded-semi-xl hover:bg-primary-color-50 active:bg-primary-color-100 cursor-pointer'
								onClick={() =>
									setCurrentCurrency(
										key as keyof typeof currencies,
									)
								}>
								{`${key} - ${value}`}
								<div
									className={cn(
										'size-4 border-primary-color rounded-full transition-all',
										currentCurrency === key
											? 'border-4'
											: 'border',
									)}
								/>
							</button>
						))}
					</div>
				)}
			</DropdownMenuContent>
		</DropdownMenu>
	);
};

export default ESelectLanguage;
